import { useFormikContext } from 'formik'
import React, { ReactElement, useContext } from 'react'

import { CheckoutSteps } from '@enums'
import amplitude from '@lib/analytics/amplitude'
import Price from '@pages/Checkout/BookingDetails/Price'
import DefaultPayButton from '@pages/Checkout/BookingDetails/SubmitSection/PayButton/Default'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { useFinalPrice } from '@pages/Checkout/hooks/usePrice'
import { PluginContext } from '@plugins/index'
import { useCheckout } from '@stores/checkout'

import '@pages/Checkout/BookingDetails/SubmitSection/index.scss'

interface Props {
  showTotalPrice?: boolean
}

const SubmitSection = ({ showTotalPrice = true }: Props): ReactElement => {
  const [{ paymentInstance, step }] = useCheckout()
  const {
    hooks: { useVisiblePrice = useFinalPrice },
  } = useContext(PluginContext)
  const {
    values: { isReservationLoading, isVacancyLoading, installment, isPaymentDetailsLoading },
    isSubmitting,
    isValid,
    errors,
    setFieldValue,
  } = useFormikContext<CheckoutFormData>()
  const isPriceLoading = isReservationLoading || isVacancyLoading
  const price = useVisiblePrice()
  const showPayButton = isPriceLoading || price
  const isConfirm = price && price.fractional <= 0
  const showProceedToPayment = step === CheckoutSteps.Details

  const handleClick = () => price && setFieldValue('visiblePrice', price)

  const handleAmplitudeEvent = (): void => {
    amplitude.checkout.clickPay(isValid, errors)
  }

  return (
    <div className="submit-section">
      {showTotalPrice && (
        <Price price={price} isLoading={isPriceLoading || isPaymentDetailsLoading} installment={installment} />
      )}
      <div data-tag="pay" onClick={handleClick} onClickCapture={handleAmplitudeEvent}>
        {showPayButton &&
          (paymentInstance?.getPayButton?.() ?? (
            <DefaultPayButton
              loading={isSubmitting || isPriceLoading}
              disabled={isSubmitting || isPriceLoading}
              confirm={!!isConfirm}
              proceedToPayment={showProceedToPayment}
            />
          ))}
      </div>
    </div>
  )
}

export default SubmitSection
